import { FC, useState, useEffect } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import {
  AppBar,
  Autocomplete,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon
} from "@mui/icons-material";
import BackdropPrimaryMain from "components/BackdropPrimaryMain";
import { useAppDispatch, useAppSelector } from "store";
import { handleClose, setValue, createEvent } from "store/slices/dialogs/createEvent.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { StyledContainer } from "components/StyledContainer";
import PrimaryButton from "components/PrimaryButton";
import { AsolviSwitch } from "components/AsolviSwitch";
import StyledTextField from "components/StyledTextField";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { asyncQueries } from "store/slices/dialogs/createEvent.queries";
import { getJobs } from "store/slices/jobs.store";
import { unwrapResult } from "@reduxjs/toolkit";

interface AdhocEventDialogProps {
  open: boolean;
  onClose: () => void;
}

interface DateFieldProps {
  label: string;
  value: Date | null;
  onChange: (newValue: Date | null) => void;
}

const DateField: FC<DateFieldProps> = ({ label, value, onChange }) => (
  <LocalizationProvider dateAdapter={AdapterDateFns}>
    <DateTimePicker
      label={label}
      value={value}
      onChange={onChange}
      ampm={false} 
      format="dd/MM/yyyy HH:mm"
      slotProps={{ textField: { fullWidth: true, required: true } }}
    />
  </LocalizationProvider>
);

export const AdhocEventDialog: FC<AdhocEventDialogProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const jobs = useAppSelector((state) => state.jobs.jobs);
  const jobWithEngineer = Object.values(jobs).find(job => job.engineerName);
  const engineerName = jobWithEngineer?.engineerName || "Unknown Engineer";
  const engineerId = jobWithEngineer?.engineerId ? parseInt(jobWithEngineer.engineerId, 10) : null;
  const [createEventLoading, setCreateEventLoading] = useState(false);

  const { selectedCategory, description, allDayToggle, eventStartDate, eventEndDate } = useAppSelector(
    (state) => state.createEvent.formValues
  );
  const { createEventLoading: storeLoading, categories } = useAppSelector((state) => state.createEvent);
  const isFormValid = description && selectedCategory && eventStartDate && eventEndDate;

  useEffect(() => {
    if (open) {
      dispatch(asyncQueries.getCreateEventCategories());
    }
  }, [open, dispatch]);

  const close = () => {
    onClose();
    dispatch(handleClose());
  };  

  const handleAllDayToggle = () => {
    dispatch(setValue({ field: "allDayToggle", value: !allDayToggle }));
  
    if (!allDayToggle && eventStartDate) {
      const startDate = new Date(eventStartDate);
      startDate.setHours(0, 0, 0, 0); 
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 1); 
      endDate.setHours(0, 0, 0, 0); 
      dispatch(setValue({ field: "eventStartDate", value: startDate }));
      dispatch(setValue({ field: "eventEndDate", value: endDate }));
    } else {
      dispatch(setValue({ field: "eventStartDate", value: null }));
      dispatch(setValue({ field: "eventEndDate", value: null }));
    }
  };
  

  const handleDateChange = (newDate: Date | null, isStartDate: boolean) => {
    if (newDate) {
      const updatedDate = new Date(newDate);
  
      if (isStartDate) {
        dispatch(setValue({ field: "eventStartDate", value: updatedDate }));
  
        if (allDayToggle) {
          const endDate = new Date(updatedDate);
          endDate.setDate(updatedDate.getDate() + 1);
          dispatch(setValue({ field: "eventEndDate", value: endDate }));
        }
      } else {
        dispatch(setValue({ field: "eventEndDate", value: updatedDate }));
  
        if (allDayToggle) {
          const startDate = new Date(updatedDate);
          startDate.setDate(updatedDate.getDate() - 1);
          dispatch(setValue({ field: "eventStartDate", value: startDate }));
        }
      }
    }
  };

  const handleSubmit = () => {
    if (!description || !selectedCategory || !eventStartDate || !eventEndDate) {
      dispatch(addSnackbarMessage({ key: "CreateEvent-fail" }));
      return;
    }
  
    if (eventEndDate && eventStartDate && eventEndDate < eventStartDate) {
      dispatch(addSnackbarMessage({ key: "CreateEvent-fail" }));
      return;
    }
  
    setCreateEventLoading(true);
  
    dispatch(
      createEvent({
        description,
        eventStartDate,
        eventEndDate,
        selectedCategory,
        engineerName,
        engineerId,
      })
    )
      .then(unwrapResult)
      .then((eventData) => {
        dispatch(addSnackbarMessage({ key: "CreateEvent-success" }));
        dispatch(getJobs({ force: true }))
          .then(unwrapResult)
          .then(() => {
            close();
          })
          .catch((e) => {
            console.error("Error fetching jobs:", e);
          });
      })
      .catch((error) => {
        dispatch(addSnackbarMessage({ key: "CreateEvent-fail" }));
        console.error("Error creating event:", error);
      })
      .finally(() => {
        setCreateEventLoading(false);
      });
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      data-testid="addAdhocEventDialog"
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '70vh', 
          height: 'auto',   
          m: '10vh auto',     
        },
      }}
    >
      <BackdropPrimaryMain open={createEventLoading || storeLoading}>
        <CircularProgress color="inherit" />
      </BackdropPrimaryMain>
      <AppBar position="sticky" elevation={1} sx={{ borderRadius: 1 }}>
      <Toolbar>
        <IconButton
          edge="start"
          color="inherit"
          onClick={close}
          aria-label={intl.formatMessage({ id: "general.back" })}
          size="large"
          data-testid="createEventCloseButton"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" marginLeft="16px" flex={1}>
          <FormattedMessage id="menu.addNewAdhocEvent" />
        </Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={close}
          aria-label="close"
          data-testid="createEventTopRightCloseButton"
        >
          <CloseIcon />
        </IconButton>
      </Toolbar>
    </AppBar>
      <StyledContainer
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 3,
        }}
      >
        {!storeLoading && (
          <Grid container direction="column" spacing={3}>
            <Grid item>
              {storeLoading ? (
                <CircularProgress />
              ) : (
                <Autocomplete
                  options={categories ? categories.map((category) => ({
                    label: category.text,
                    id: category.id,
                  })) : []}
                  value={selectedCategory ? { id: selectedCategory.id, label: selectedCategory.text } : null}               
                  onChange={(e, value) => {
                    if (value) {
                      dispatch(setValue({ field: "selectedCategory", value: { id: value.id, text: value.label } }));
                    }
                  }}
                  getOptionLabel={(option) => option.label}
                  renderInput={(params) => (
                    <TextField {...params} label={intl.formatMessage({ id: "createEvent.category" })} fullWidth required />
                  )}
                  noOptionsText={intl.formatMessage({ id: "createEvent.noCategories" })}
                />
              )}
            </Grid>
            <Grid item>
              <StyledTextField
                name="description"
                fullWidth
                label={intl.formatMessage({ id: "createEvent.description" })}
                multiline
                rows={5}
                value={description || ""}
                onChange={(e) => dispatch(setValue({ field: "description", value: e.target.value }))}
                required
              />
            </Grid>
            <Grid item container justifyContent="space-between" alignItems="center">
              <Typography>
                <FormattedMessage id="addAdhoc.fullDay" />
              </Typography>
              <AsolviSwitch
                checked={allDayToggle || false}
                onChange={handleAllDayToggle}
              />
            </Grid>
            <Grid container spacing={3} paddingTop={3} paddingLeft={3}>
              <Grid item xs={12} sm={6} sx={{ paddingLeft: 2 }}>
              <DateField
                  label={intl.formatMessage({ id: "createEvent.startDate" })}
                  value={eventStartDate}
                  onChange={(newDate) => handleDateChange(newDate, true)} 
                />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ paddingLeft: 2 }}>
              <DateField
                  label="End Date"
                  value={eventEndDate}
                  onChange={(newDate) => handleDateChange(newDate, false)} 
                />
              </Grid>
            </Grid>
            <Grid item>
            <PrimaryButton
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={
                !isFormValid ||
                createEventLoading ||
                (eventEndDate && eventStartDate && eventEndDate < eventStartDate) 
              }
            >
              {intl.formatMessage({ id: "general.add" })}
            </PrimaryButton>
          </Grid>
          </Grid>
        )}
      </StyledContainer>
    </Dialog>
  );
};
